import { ChevronDownIcon, SearchIcon } from 'lucide-react'
import { Popover as PopoverPrimitive } from 'radix-ui'
import { useState } from 'react'

import { type DocumentDirection, useDocumentDirection } from '../../hooks/use-document-direction'
import { fieldTriggerClass } from '../../lib/field-trigger'
import { rankOptions } from '../../lib/option-match'
import { cn } from '../../lib/utils'
import { Button } from './button'
import { Checkbox } from './checkbox'
import { Input } from './input'

export interface MultiSelectOption {
  value: string
  label: string
  /** Searched, never shown: a code that finds a row its label does not spell out. */
  keywords?: string[]
  /** Indents the row in the list by `depth` rem; the trigger stays flat. */
  depth?: number
  /** Listed but not tickable, e.g. a group. */
  disabled?: boolean
}

export interface MultiSelectProps {
  values: string[]
  onValuesChange: (values: string[]) => void
  options: MultiSelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyLabel?: string
  clearLabel?: string
  /**
   * Formats the "N selected" trigger label. The default is English; a caller
   * with an Arabic UI feeds this from its own locale files so the package
   * itself stays i18n-free.
   */
  selectedCountLabel?: (count: number) => string
  /**
   * Extra classes for the trigger button. The default width (`w-64`) suits a
   * roomy toolbar; a crowded one (employees: search + four filters) narrows
   * each trigger with `w-48 shrink-0` so all five sit on one row.
   */
  className?: string
  dir?: DocumentDirection
  'aria-label'?: string
  onSearchChange?: (search: string) => void
}

function MultiSelect({
  values,
  onValuesChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  clearLabel,
  selectedCountLabel,
  onSearchChange,
  className,
  dir: providedDir,
  ...props
}: MultiSelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  // Portalled content lands on document.body, outside any wrapper in the tree —
  // it must carry the direction itself, including after a locale switch.
  const documentDirection = useDocumentDirection()
  const dir = providedDir ?? documentDirection

  const selectedLabels = options
    .filter((option) => values.includes(option.value))
    .map((option) => option.label)

  const filtered = rankOptions(options, query)

  const toggle = (optionValue: string) => {
    onValuesChange(
      values.includes(optionValue)
        ? values.filter((v) => v !== optionValue)
        : [...values, optionValue],
    )
  }

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) {
          setQuery('')
          onSearchChange?.('')
        }
      }}
    >
      <PopoverPrimitive.Trigger asChild>
        {/* A native button, not the `Button` primitive — the same reason as
            `Combobox`. This picks values into a form, so it takes the field
            skin. See `lib/field-trigger.ts`. */}
        {/* No `role="combobox"`, unlike `Combobox`. This pass changes how the
            control LOOKS, not what it announces, and the two are separate
            decisions. The trigger is a button with `aria-expanded` today; a
            combobox role also needs `aria-controls` and a listbox id on the
            portalled panel, which is its own piece of work. */}
        <button
          type="button"
          aria-expanded={open}
          data-placeholder={values.length === 0 ? '' : undefined}
          className={fieldTriggerClass('w-64', className)}
          {...props}
        >
          <span className="truncate">
            {values.length === 0
              ? placeholder
              : // Up to two selections are readable as a list; beyond that the
                // count carries the information. The `||` fallback covers
                // server-filtered options whose labels have not loaded yet.
                // The count string is localised through `selectedCountLabel`;
                // without it the English default is byte-identical to the
                // pre-prop behaviour.
                values.length <= 2
                ? selectedLabels.join(', ') ||
                  (selectedCountLabel?.(values.length) ?? `${values.length} selected`)
                : (selectedCountLabel?.(values.length) ?? `${values.length} selected`)}
          </span>
          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          dir={dir}
          align="start"
          sideOffset={4}
          data-slot="multi-select-content"
          className={cn(
            'z-50 w-[max(16rem,var(--radix-popover-trigger-width))] max-w-[calc(100vw-2rem)] overflow-hidden p-0',
            // The popover elevation (DESIGN.md §4).
            'rounded-md border border-border bg-popover text-popover-foreground shadow-popover',
            'duration-[var(--motion-base)] data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        >
          <div className="border-b border-border p-2">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                aria-label={searchPlaceholder}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value)
                  onSearchChange?.(event.target.value)
                }}
                placeholder={searchPlaceholder}
                className="ps-8"
              />
            </div>
          </div>
          <ul aria-label={props['aria-label']} className="max-h-60 overflow-y-auto p-1">
            {filtered.length === 0 && (
              <li className="px-2.5 py-2 text-body text-muted-foreground">{emptyLabel}</li>
            )}
            {filtered.map((option) => (
              <li key={option.value}>
                {/* A checked option is grey ink plus its tick, kept under the
                    pointer (DESIGN.md §5 "The soak"; lead 5 adds the motion). */}
                {/* biome-ignore lint/a11y/noLabelWithoutControl: the "control"
                is a Radix Checkbox (a button[role=checkbox]); the wrapping
                label is what gives it an accessible name and a large click
                target. */}
                <label
                  data-slot="multi-select-option"
                  className={cn(
                    'flex min-h-9 w-full cursor-default items-center gap-2 rounded-sm px-2.5 py-2 text-body transition-ui select-none',
                    'hover:bg-accent',
                    'has-[[data-state=checked]]:bg-brand-subtle has-[[data-state=checked]]:text-brand-subtle-foreground',
                    option.disabled &&
                      'cursor-not-allowed text-muted-foreground hover:bg-transparent',
                  )}
                >
                  <Checkbox
                    checked={values.includes(option.value)}
                    disabled={option.disabled}
                    onCheckedChange={() => toggle(option.value)}
                    style={option.depth ? { marginInlineStart: `${option.depth}rem` } : undefined}
                  />
                  {option.label}
                </label>
              </li>
            ))}
          </ul>
          {values.length > 0 && (
            <div className="border-t border-border p-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => {
                  onValuesChange([])
                  setOpen(false)
                }}
              >
                {clearLabel}
              </Button>
            </div>
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}

export { MultiSelect }
